import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Activation } from "../api/models"
import { addEntityFetchReducers, EntityState } from "./entity"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { licenseCollectionThunk, licenseThunk } from "./licensesSlice"
import { useFetchableState } from "./fetchableState"
import { redemptionsThunk, redemptionThunk } from "./redemptionsSlice"

export interface ActivationsState extends EntityState<Activation> {
    debug: string
}

const initialState: ActivationsState = {
    entities: {},
    debug: "activations state"
}

export const activationsSlice = createSlice({
    name: 'activations',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Activation,
            { activation: Activation },
            { id: string, accessToken: string }
        >(
            builder,
            (payload) => payload.activation,
            activationThunk
        )
        builder.addCase(logOut, (state: ActivationsState) => {
            state = initialState;
        })
        builder.addCase(licenseThunk.fulfilled, (state, action) => {
            const { activations } = action.payload
            activations.forEach(activation => {
                state.entities[activation.id] = {
                    data: activation,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(licenseCollectionThunk.fulfilled, (state, action) => {
            const { activations } = action.payload
            activations.forEach(activation => {
                state.entities[activation.id] = {
                    data: activation,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(redemptionThunk.fulfilled, (state, action) => {
            const { activations } = action.payload
            activations.forEach(activation => {
                state.entities[activation.id] = {
                    data: activation,
                    lastFetched: Date.now()
                }
            })
        })    
        builder.addCase(redemptionsThunk.fulfilled, (state, action) => {
            const { activations } = action.payload
            activations.forEach(activation => {
                state.entities[activation.id] = {
                    data: activation,
                    lastFetched: Date.now()
                }
            })
        })          
        builder.addCase(revokeActivationThunk.fulfilled, (state, action) => {
            const { activation } = action.payload
            state.entities[activation.id] = {
                data: activation,
                lastFetched: Date.now()
            }
        })
        // the name is device-scoped, not activation-scoped: patch every cached
        // activation of the renamed device (matches the backend join semantics)
        builder.addCase(renameDeviceThunk.fulfilled, (state, action) => {
            const { device } = action.payload
            for (const entity of Object.values(state.entities)) {
                const activation = entity?.data
                if (activation && activation.deviceId === device.deviceId)
                    entity.data = { ...activation, deviceName: device.deviceName }
            }
        })
    },
})


// GET activations/:activationId

export const activationThunk = createAsyncThunk(
    'activations/fetchActivation',
    async ({ id, accessToken }: { id: string, accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.activations.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getActivation({ activationId: id, accessToken })
    }
)

export function useActivation(id: string, accessToken: string) {
    return useFetchableState<Activation>({
        selector: (state: RootState) => state.activations.entities[id],
        action: activationThunk({ id, accessToken })
    })
}

// PATCH devices/:deviceId (rename)
export const renameDeviceThunk = createAsyncThunk(
    'activations/renameDevice',
    async ({ deviceId, deviceName, accessToken }: { deviceId: string, deviceName: string, accessToken: string }) => {
        return await Api.patchDevice({ deviceId, deviceName, accessToken })
    }
)

// DELETE activations/:activationId (deacrivate)
export const revokeActivationThunk = createAsyncThunk(
    'activations/revokeActivation',
    async ({ activationId, accessToken }: { activationId: string, accessToken: string }) => {
        return await Api.revokeActivation({ activationId, accessToken })
    }
)

export default activationsSlice.reducer